import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Separator } from '../../components/ui/separator';

/**
 * Shared layout for standalone legal pages: /privacy, /terms, /disclaimer.
 * Light premium aesthetic, matches marketing site typography.
 */
export const LegalLayout = ({ title, lastUpdated, eyebrow = 'Legal', children, testId }) => {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'instant' });
      document.title = `${title} | Apex Athletic Mind Consulting LLC`;
    }
  }, [title]);

  return (
    <main
      className="min-h-screen bg-[#FAFAF8] text-[#1a1a1a]"
      data-testid={testId || 'legal-page'}
    >
      {/* Top bar */}
      <div className="border-b border-black/8 bg-white">
        <div className="max-w-[820px] mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[13px] text-[#3a3a3a] hover:text-[#004C54] transition-colors"
            data-testid="legal-back-home"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Apex Athletic Mind
          </Link>
          <span className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#004C54] font-semibold">
            {eyebrow}
          </span>
        </div>
      </div>

      {/* Content */}
      <article className="max-w-[760px] mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <header className="mb-10">
          <h1
            className="font-heading text-[2rem] sm:text-[2.4rem] font-bold tracking-tight text-[#1a1a1a]"
            data-testid="legal-page-title"
          >
            {title}
          </h1>
          {lastUpdated && (
            <p
              className="mt-3 text-[13px] text-[#6b6b6b]"
              data-testid="legal-last-updated"
            >
              {lastUpdated}
            </p>
          )}
        </header>

        <div
          className="prose-legal text-[15.5px] leading-[1.75] text-[#2a2a2a] space-y-7"
          data-testid="legal-content"
        >
          {children}
        </div>

        <Separator className="mt-14 mb-6 bg-black/10" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-[12.5px] text-[#6b6b6b]">
          <p>
            &copy; {new Date().getFullYear()} Apex Athletic Mind Consulting LLC. All rights reserved.
          </p>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-[#004C54] transition-colors" data-testid="legal-footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-[#004C54] transition-colors" data-testid="legal-footer-terms">Terms</Link>
            <Link to="/disclaimer" className="hover:text-[#004C54] transition-colors" data-testid="legal-footer-disclaimer">Disclaimer</Link>
          </div>
        </div>
      </article>
    </main>
  );
};

export const LegalSection = ({ heading, children, testId }) => (
  <section data-testid={testId}>
    {heading && (
      <h2 className="font-heading text-[1.15rem] sm:text-[1.25rem] font-semibold text-[#1a1a1a] mb-3">
        {heading}
      </h2>
    )}
    <div className="space-y-3">{children}</div>
  </section>
);

export default LegalLayout;
